<template>
  <div>
    <div class="d-flex justify-content-between w-100">
      <h5 class="m-0">
        <span
          class="badge badge-dark badge-pill mr-1"
          v-if="index"
        >
          {{ index }}
        </span><span v-html="formattedItemText(title)"/>

        <v-button
          v-if="canCopyTitle"
          :icon="copied ? 'check' : 'copy'"
          style-type="link"
          size="xs"
          :title="$t('phrase.copy_title')"
          @click="copyToClipBoard(title)"
        />
      </h5>
    </div>

    <div class="mb-2 text-muted">{{ item.help_text }}</div>

    <div v-if="isMedia(item.render_type)">
      <v-youtube
        v-if="item.is_video && item.youtube_id"
        :video-id="item.youtube_id"
        player-width="100%"
        player-height="200px"
      />

      <v-audio
        v-else-if="item.is_audio && item.media_file"
        :source="item.media_file"
      />

      <v-image
        v-if="item.is_image && item.media_file"
        :source="item.media_file"
      />
    </div>
    <div v-else-if="item.render_type === 'text'">
      <v-text-area
        :disabled="true"
      />
    </div>
    <div v-else-if="item.render_type === 'input'">
      <v-text-input
        :disabled="true"
      />
    </div>
    <div v-else-if="isChoice(item.render_type)">
      <table v-if="options.length > 0">
        <tr
          v-for="option in options"
          :key="option.value"
        >
          <td>
            <div class="d-flex justify-content-center">
              <img
                v-if="option.image"
                alt="image"
                class="img-item-option mr-2"
                :src="option.image"
              />
              <i
                class="fa fa-circle mr-2"
                v-else-if="isSingleChoice(item.render_type)"
              ></i>
              <i
                class="fa fa-square mr-2"
                v-else-if="isMultiChoice(item.render_type)"
              ></i>
            </div>
          </td>
          <td>
            {{ option.value }}
          </td>
        </tr>

        <tr v-if="item.config.exclusive_response">
          <td>
            <div class="d-flex justify-content-center">
              <i
                class="fa fa-square mr-2 mb-2"
              ></i>
            </div>
          </td>
          <td>
            {{ $t(EXCLUSIVE_RESPONSE_OPTIONS[item.config.exclusive_response]) }}
            <div class="badge badge-secondary">
              {{ $t('word.exclusive_response') }}
            </div>
          </td>
        </tr>

        <tr v-if="item.config.allow_other">
          <td>
            <div class="d-flex justify-content-center">
              <i
                v-if="isMultiChoice(item.render_type)"
                class="fa fa-square mr-2 mb-2"
              ></i>
              <i
                v-if="isSingleChoice(item.render_type)"
                class="fa fa-circle mr-2 mb-2"
              ></i>
            </div>
          </td>
          <td>
            {{ $t('phrase.else_namely') }}...
          </td>
        </tr>
      </table>
      <div
        class="alert alert-warning"
        v-else
      >
        <em>{{ $t('sentence.no_options_added') }}</em>
      </div>

      <div class="mt-2 d-flex">
        <div class="badge badge-secondary mr-1" v-if="item.config.random">
          <i class="fa fa-random mr-1"></i>
          {{ $t('phrase.random_order') }}
        </div>
        <div class="badge badge-secondary mr-1">
          <i
            class="fa mr-1"
            :class="[item.config.direction === 'vertical' ? 'fa-arrow-down' : 'fa-arrow-right']"
          ></i>
          {{ $t(`word.${item.config.direction}`) }}
        </div>
        <div
          v-if="item.config.min_options"
          class="badge badge-secondary mr-1"
        >
          <i class="fa fa-list-check"></i>
          Min. {{ item.config.min_options }}
        </div>
        <div
          v-if="item.config.max_options"
          class="badge badge-secondary mr-1"
        >
          <i class="fa fa-list-check"></i>
          Max. {{ item.config.max_options }}
        </div>
      </div>
    </div>
    <div
      v-else-if="item.render_type === 'stars'"
    >
      <div
        class="d-flex flex-nowrap w-100 justify-content-between mb-1"
        style="max-width: 300px"
      >
        <i
          class="far fa-star text-cr-red"
          v-for="choice in [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]"
          :key="choice"
        ></i>
      </div>
      <div
        class="d-flex justify-content-between w-100"
        style="max-width: 300px"
      >
        <div class="text-muted">
          <small v-if="item.config.min_text">
            {{ item.config.min_text }}
          </small>
        </div>

        <div class="text-muted">
          <small v-if="item.config.max_text">
            {{ item.config.max_text }}
          </small>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TextArea from '@/common/components/inputs/TextArea.vue';
import TextInput from '@/common/components/inputs/TextInput.vue';
import {
  EXCLUSIVE_RESPONSE_OPTIONS,
  formattedItemText,
  isChoice,
  isMedia,
  isMultiChoice,
  isSingleChoice,
} from '@/common/services/items';
import { YouTubePlayer } from 'vue-youtube-embed';
import Audio from '@/common/components/Audio.vue';
import Button from '@/common/components/Button.vue';
import Image from '@/common/components/Image.vue';

export default {
  name: 'Item',
  props: {
    canCopyTitle: {
      type: Boolean,
      default: false,
    },
    index: {
      type: Number,
      required: false,
    },
    item: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
  },
  components: {
    'v-audio': Audio,
    'v-button': Button,
    'v-image': Image,
    'v-text-area': TextArea,
    'v-text-input': TextInput,
    'v-youtube': YouTubePlayer,
  },
  methods: {
    formattedItemText,
    isChoice,
    isMultiChoice,
    isSingleChoice,
    isMedia,
    copyToClipBoard(title) {
      navigator.clipboard.writeText(title).then(() => {
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 1000);
      }, (err) => {
        console.error('Async: Could not copy text: ', err);
      });
    },
  },
  data() {
    return {
      EXCLUSIVE_RESPONSE_OPTIONS,
      copied: false,
    };
  },
  computed: {
    options() {
      return this.item.options || [];
    },
  },
};
</script>
